"use client";

import Size from "@JohnLewisPartnership/jl-design-system/dist/components/size/Size";
import SizeList from "@JohnLewisPartnership/jl-design-system/dist/components/size/SizeList";
import { useEffect, useRef, useState } from "react";
import { Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/v2/Message";
import smoothscroll from "smoothscroll-polyfill";
import Link, { LinkProps } from "next/link";
import {
  NO_VARIANT_CODE,
  SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED,
  WARNING_OR_ERROR_EVENT,
} from "../../constants/constants";
import styles from "./Sizes.module.scss";

type Props = {
  /**
   * An array of unique sizes, each having a name, sublabel, sizeReductionLabel, showReduced, unavailable, and isSelected properties.
   */
  uniqueSizes: {
    name: string | null | undefined;
    sublabel?: string | null | undefined;
    sizeReductionLabel: string;
    showReduced: boolean;
    unavailable: boolean;
    isSelected: boolean;
    url: LinkProps["href"];
  }[];
  /**
   * A function that handles click events, taking a string value as an argument.
   */
  handleClick: (value: string) => void;
};

/**
 * Renders a list of unique sizes for a product variant.
 */
export function SizesUI({ uniqueSizes: uniqueSizes }: Props) {
  const [hasSizeError, setHasSizeError] = useState(false);
  const errorMessageRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => setHasSizeError(false), [uniqueSizes]);

  useEffect(() => {
    const handleNoSizeSelectedError = () => {
      setHasSizeError(true);
    };

    const errorOrWarningHandler = (
      event: CustomEvent<{ errors: { code: string }[] }>,
    ) => {
      const data = event.detail;
      const { errors = [] } = data;
      const hasVariantError = errors.some(
        ({ code }) => code === NO_VARIANT_CODE,
      );

      if (hasVariantError) {
        setHasSizeError(true);
      }
    };

    window.addEventListener(
      SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED,
      handleNoSizeSelectedError,
    );

    window.addEventListener(WARNING_OR_ERROR_EVENT, errorOrWarningHandler);

    return () => {
      window.removeEventListener(
        SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED,
        handleNoSizeSelectedError,
      );
      window.removeEventListener(WARNING_OR_ERROR_EVENT, errorOrWarningHandler);
    };
  }, []);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (hasSizeError) {
      smoothscroll.polyfill();
      const sizeErrorElement = errorMessageRef.current;
      if (sizeErrorElement) {
        const headerOffset = parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue(
            "--jl-header-height",
          ),
        );
        const elementPosition = sizeErrorElement.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    }
  }, [hasSizeError]);

  if (!uniqueSizes.length) {
    return null;
  }

  return (
    <>
      {hasSizeError && (
        <div id="size:error" data-testid="size:error" ref={errorMessageRef}>
          <Message
            type="error"
            title="Select a size"
            className={styles.message}
          />
        </div>
      )}
      <SizeList
        selectedLabel={uniqueSizes.filter((size) => size.isSelected)[0]?.name}
        sublabel={uniqueSizes.filter((size) => size.isSelected)[0]?.sublabel}
        onClick={() => {
          setHasSizeError(false);
        }}
      >
        {uniqueSizes.map((size, index) => (
          <Size
            key={`size-${index}`}
            id={`size-${size.name}`}
            data-testid="size:option"
            unavailable={size.unavailable}
            value={size.name}
            selected={size.isSelected}
            hasReducedPrice={size.showReduced}
            reducedPriceText={size.sizeReductionLabel}
            minimiseLabelPadding={true}
            Tag={Link}
            buttonProps={{
              href: size.url,
              shallow: true,
              scroll: false,
            }}
          >
            {size.name}
          </Size>
        ))}
      </SizeList>
    </>
  );
}
